/**
 * SR Lens Technology section: tab panels + before/after image compare per panel.
 */
(function () {
  function initCompare(root) {
    root.querySelectorAll('sr-image-comparison').forEach((comparison) => {
      if (comparison.dataset.srCompareInitialized === '1') return;

      const slider = comparison.querySelector('.comparison-slider');
      const afterLayer = comparison.querySelector(
        '.comparison-slider__layer--after'
      );
      const handle = comparison.querySelector('.slider-handle');

      if (!slider || !afterLayer || !handle) return;

      comparison.dataset.srCompareInitialized = '1';

      let dragging = false;
      let currentPct = 50;

      function setPosition(pct) {
        const value = Math.max(0, Math.min(100, pct));

        currentPct = value;

        afterLayer.style.setProperty(
          'clip-path',
          `inset(0 0 0 ${value}%)`,
          'important'
        );

        handle.style.setProperty('left', `${value}%`, 'important');

        handle.setAttribute('aria-valuenow', String(Math.round(value)));
        handle.setAttribute('aria-valuetext', `${Math.round(value)}%`);
      }

      function positionFromPointer(clientX) {
        const rect = slider.getBoundingClientRect();

        if (!rect.width) return;

        const pct = ((clientX - rect.left) / rect.width) * 100;

        setPosition(pct);
      }

      slider.addEventListener('pointerdown', (event) => {
        if (event.button !== 0 && event.pointerType === 'mouse') return;

        dragging = true;

        slider.setPointerCapture?.(event.pointerId);

        positionFromPointer(event.clientX);

        event.preventDefault();
      });

      slider.addEventListener('pointermove', (event) => {
        if (!dragging) return;

        positionFromPointer(event.clientX);

        event.preventDefault();
      });

      slider.addEventListener('pointerup', (event) => {
        dragging = false;

        if (slider.hasPointerCapture?.(event.pointerId)) {
          slider.releasePointerCapture(event.pointerId);
        }
      });

      slider.addEventListener('pointercancel', () => {
        dragging = false;
      });

      slider.addEventListener('lostpointercapture', () => {
        dragging = false;
      });

      /*
      * Keyboard accessibility
      */
      handle.setAttribute('tabindex', '0');
      handle.setAttribute('role', 'slider');
      handle.setAttribute('aria-valuemin', '0');
      handle.setAttribute('aria-valuemax', '100');
      if (!handle.hasAttribute('aria-label')) {
        handle.setAttribute('aria-label', 'Image comparison slider');
      }

      handle.addEventListener('keydown', (event) => {
        let next = currentPct;

        if (event.key === 'ArrowLeft' || event.key === 'ArrowDown') {
          next -= 2;
        } else if (event.key === 'ArrowRight' || event.key === 'ArrowUp') {
          next += 2;
        } else if (event.key === 'Home') {
          next = 0;
        } else if (event.key === 'End') {
          next = 100;
        } else {
          return;
        }

        event.preventDefault();

        setPosition(next);
      });

      setPosition(50);
    });
  }

  function normalizeSwymCompareLayout(panel) {
    if (!panel || panel.hasAttribute('hidden')) return;
    const slider = panel.querySelector('.comparison-slider');
    if (!slider) return;
    const imgs = slider.querySelectorAll('.comparison-slider__layer img.image-before, .comparison-slider__layer img.image-after');
    imgs.forEach((img) => {
      let node = img.parentElement;
      while (node && node !== slider) {
        if (node.nodeType === 1) {
          const attr = node.getAttribute('style');
          if (attr && /\bposition\s*:\s*relative\b/i.test(attr)) {
            node.style.setProperty('position', 'absolute', 'important');
          }
        }
        node = node.parentElement;
      }
    });
  }

  function scheduleNormalizeCompare(panel) {
    if (!panel) return;
    const run = () => normalizeSwymCompareLayout(panel);
    run();
    requestAnimationFrame(() => requestAnimationFrame(run));
    setTimeout(run, 0);
    setTimeout(run, 400);
    setTimeout(run, 1200);
  }

  function resyncVisibleCompareClips(section) {
    const CE = customElements.get('sr-image-comparison');
    if (!CE || typeof CE.applyClip !== 'function') return;
    section.querySelectorAll('.sr-lens-technology__panel:not([hidden]) sr-image-comparison').forEach((el) => {
      const handle = el.querySelector('.slider-handle');
      const slider = el.querySelector('.comparison-slider');
      if (!handle || !slider) return;
      let pct = 50;
      const left = handle.style.left;
      if (left && left.indexOf('%') !== -1) {
        const n = parseFloat(left);
        if (Number.isFinite(n)) pct = n;
      }
      CE.applyClip(el, pct);
    });
  }

  /** Swym often mutates the visible (Colorush) tab after our delayed passes — keep layout + clip in sync */
  function attachSwymMutationWatch(section) {
    if (section.dataset.srSwymCompareWatch === '1') return;
    section.dataset.srSwymCompareWatch = '1';
    let t;
    const obs = new MutationObserver(() => {
      clearTimeout(t);
      t = setTimeout(() => {
        section.querySelectorAll('.sr-lens-technology__panel:not([hidden])').forEach((panel) => {
          normalizeSwymCompareLayout(panel);
        });
        resyncVisibleCompareClips(section);
      }, 60);
    });
    obs.observe(section, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style', 'class'],
    });
  }

  function initTabs(section) {
    const tablist = section.querySelector('.sr-lens-technology__tablist');
    const tabs = section.querySelectorAll('.sr-lens-technology__tab');
    const panels = section.querySelectorAll('.sr-lens-technology__panel');
    if (!tabs.length || !panels.length) return;

    if (tablist && tabs.length === 2) {
      tablist.classList.add('sr-lens-technology__tablist--two');
    }

    function show(index) {
      tabs.forEach((t, i) => {
        t.setAttribute('aria-selected', i === index ? 'true' : 'false');
        t.setAttribute('tabindex', i === index ? '0' : '-1');
      });
      panels.forEach((p, i) => {
        if (i === index) p.removeAttribute('hidden');
        else p.setAttribute('hidden', '');
      });
      scheduleNormalizeCompare(panels[index]);
      resyncVisibleCompareClips(section);
    }

    tabs.forEach((tab, i) => {
      tab.addEventListener('click', () => show(i));
      tab.addEventListener('keydown', (event) => {
        let next = null;
        if (event.key === 'ArrowRight') next = (i + 1) % tabs.length;
        else if (event.key === 'ArrowLeft') next = (i - 1 + tabs.length) % tabs.length;
        else if (event.key === 'Home') next = 0;
        else if (event.key === 'End') next = tabs.length - 1;
        if (next === null) return;
        event.preventDefault();
        show(next);
        tabs[next].focus();
      });
    });

    show(0);
  }

  function boot(root) {
    root.querySelectorAll('.sr-lens-technology').forEach((section) => {
      initTabs(section);
      initCompare(section);
      attachSwymMutationWatch(section);
      resyncVisibleCompareClips(section);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => boot(document));
  } else {
    boot(document);
  }

  document.addEventListener('shopify:section:load', (e) => {
    if (e.target && e.target.classList && e.target.classList.contains('shopify-section')) {
      boot(e.target);
    }
  });

  /**
   * Tier visibility = Liquid (same as full refresh). Re-fetch this section with ?variant= via Section Rendering API.
   * @see https://shopify.dev/docs/api/ajax/section-rendering
   */
  function initLensTechVisibleFromServer() {
    if (document.documentElement.dataset.srLensTechServerSync === '1') return;
    document.documentElement.dataset.srLensTechServerSync = '1';

    function lensRootsForProduct(pid) {
      const out = [];
      document.querySelectorAll('.sr-lens-technology[data-sr-lens-section-id][data-sr-lens-product-id]').forEach((root) => {
        if (String(root.dataset.srLensProductId || '') !== pid) return;
        const sid = root.dataset.srLensSectionId;
        if (!sid) return;
        out.push({ root, sectionId: sid });
      });
      return out;
    }

    function productIdFromIdInput(el) {
      const f = el.closest('form.js-product-form');
      if (f && f.dataset.productId != null && f.dataset.productId !== '') return String(f.dataset.productId);
      const col = el.closest('.product-form[data-product-id]');
      if (col && col.dataset.productId != null && col.dataset.productId !== '') return String(col.dataset.productId);
      return null;
    }

    function fetchFreshLensRoot(path, sectionId, variantId) {
      const sp = new URLSearchParams();
      sp.set('sections', sectionId);
      sp.set('variant', variantId);
      const url = `${path}?${sp.toString()}`;
      return fetch(url)
        .then((res) => {
          if (!res.ok) return null;
          return res.json();
        })
        .then((data) => {
          if (!data || typeof data !== 'object') return null;
          let html = data[sectionId];
          if ((html == null || typeof html !== 'string') && Object.keys(data).length === 1) {
            html = data[Object.keys(data)[0]];
          }
          if (!html || typeof html !== 'string') return null;
          const doc = new DOMParser().parseFromString(html, 'text/html');
          return doc.querySelector('.sr-lens-technology[data-sr-lens-style-idx]');
        });
    }

    function applyLensRootVisibility(live, fresh) {
      if (!live || !fresh) return;
      live.className = fresh.className;
      if (fresh.hasAttribute('hidden')) live.setAttribute('hidden', '');
      else live.removeAttribute('hidden');
      const hiddenByClass = live.classList.contains('sr-lens-technology--variant-hidden');
      const hiddenByAttr = live.hasAttribute('hidden');
      if (!hiddenByClass && !hiddenByAttr) {
        requestAnimationFrame(() => window.dispatchEvent(new Event('resize')));
      }
    }

    function runFetchesForProduct(pid, variantId) {
      const vid = String(variantId || '').trim();
      if (!vid) return;
      const path = window.location.pathname;
      const entries = lensRootsForProduct(pid);
      if (!entries.length) return;
      entries.forEach(({ root, sectionId }) => {
        fetchFreshLensRoot(path, sectionId, vid)
          .then((fresh) => applyLensRootVisibility(root, fresh))
          .catch(() => {});
      });
    }

    let debounce;
    function schedule(pid, variantId) {
      clearTimeout(debounce);
      debounce = setTimeout(() => runFetchesForProduct(pid, variantId), 100);
    }

    document.addEventListener(
      'change',
      (e) => {
        const t = e.target;
        if (!t || t.name !== 'id') return;
        const tag = t.tagName;
        if (tag !== 'INPUT' && tag !== 'SELECT') return;
        const pid = productIdFromIdInput(t);
        if (!pid || !lensRootsForProduct(pid).length) return;
        schedule(pid, t.value);
      },
      true
    );

    document.addEventListener('on:variant:change', (e) => {
      const form = e.detail && e.detail.form;
      if (!form || form.dataset.productId == null || form.dataset.productId === '') return;
      const pid = String(form.dataset.productId);
      if (!lensRootsForProduct(pid).length) return;
      const v = e.detail.variant;
      let vid =
        v && v.id != null
          ? String(v.id)
          : String(form.querySelector('input[name="id"], select[name="id"]')?.value || '').trim();
      if (!vid) {
        vid = new URLSearchParams(window.location.search).get('variant') || '';
      }
      if (!vid) return;
      schedule(pid, vid);
    });
  }

  initLensTechVisibleFromServer();
})();
